import { FiMic, FiMicOff } from "react-icons/fi";
import { LuVideo, LuVideoOff } from "react-icons/lu";
import { MdOutlineCallEnd } from "react-icons/md";
import { HiSpeakerWave, HiSpeakerXMark } from "react-icons/hi2";
import { IoChatbubbles } from "react-icons/io5";
import { HiOutlineUsers } from "react-icons/hi2";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks";
import { updatePeerData } from "../../../store/Slices/PeerJsSlice";
import { updateViewState } from "../../../store/Slices/ViewManagerSlice";
import { socketInstance } from "../../../socket/socket";
import { useTheme } from "../../../context/ThemeProvider";
import { useIsGroupCall } from "../../../hooks/useIsGroupCall";
import {
  beginCallTeardown,
  idlePeerCallPatch,
  leaveCallToChat,
} from "../../../utils/endCallSession";

export default function Controlls() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const socket = socketInstance();
  const { theme } = useTheme();
  const livekitPeerId =
    typeof window !== "undefined"
      ? sessionStorage.getItem("livekit_peer_id") || ""
      : "";
  const isGroupCall = useIsGroupCall();
  const showParticipants = useAppSelector(
    (s) => s.ViewManager.show_chat_in_call,
  );
  const {
    isVideoEnabled,
    isAudioEnabled,
    isRemoteMuted,
    call_id,
    chat_id,
    room_id,
  } = useAppSelector((state) => state.PeerJsSlice);
  const call_type = sessionStorage.getItem("call_type");

  function endCall() {
    beginCallTeardown();
    socket.emit("leave_call", {
      call_id,
      peer_id: livekitPeerId,
      call_type,
      chat_id,
    });
    dispatch(updatePeerData(idlePeerCallPatch()));
    leaveCallToChat();
  }

  function goToChat() {
    dispatch(updateViewState({ show_chat_in_call: false }));
    navigate("/chat");
  }

  function toggleParticipants() {
    dispatch(updateViewState({ show_chat_in_call: !showParticipants }));
  }

  const toggleAudio = () => {
    socket.emit("call_changes", {
      room_id,
      peer_id: livekitPeerId,
      isAudioEnabled: !isAudioEnabled,
      isVideoEnabled: call_type == "audio" ? false : isVideoEnabled,
    });
    dispatch(updatePeerData({ isAudioEnabled: !isAudioEnabled }));
  };

  const toggleVideo = () => {
    socket.emit("call_changes", {
      room_id,
      peer_id: livekitPeerId,
      isVideoEnabled: !isVideoEnabled,
    });
    dispatch(updatePeerData({ isVideoEnabled: !isVideoEnabled }));
  };

  const toggleRemoteMute = () => {
    dispatch(updatePeerData({ isRemoteMuted: !isRemoteMuted }));
  };

  const btnBase =
    "grid place-content-center rounded-full border transition-all duration-200 active:scale-95 focus:outline-none focus:ring-2";
  const btnSize = "h-12 w-12 sm:h-14 sm:w-14";

  const btnOn =
    theme === "dark"
      ? "border-white/15 bg-white/10 text-white hover:bg-white/20 backdrop-blur-md focus:ring-white/30"
      : "border-black/10 bg-black/5 text-darkText hover:bg-black/10 backdrop-blur-md focus:ring-black/20";
  const btnOff =
    "border-red-400/30 bg-red-500/90 text-white hover:bg-red-500 shadow-lg shadow-red-500/30 focus:ring-red-400/40";

  const barCls =
    theme === "dark"
      ? "border-white/10 bg-black/50"
      : "border-black/10 bg-white/70";

  return (
    <div
      className="absolute inset-x-0 bottom-4 z-20 flex justify-center px-4 sm:bottom-6"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div
        className={`flex items-center gap-2.5 rounded-2xl border px-3 py-3 shadow-2xl backdrop-blur-2xl sm:gap-4 sm:px-6 sm:py-3.5 ${barCls}`}
      >
        <button
          type="button"
          aria-label={isAudioEnabled ? "Mute microphone" : "Unmute microphone"}
          onClick={toggleAudio}
          className={`${btnBase} ${btnSize} ${isAudioEnabled ? btnOn : btnOff}`}
        >
          {isAudioEnabled ? (
            <FiMic className="text-xl sm:text-2xl" />
          ) : (
            <FiMicOff className="text-xl sm:text-2xl" />
          )}
        </button>

        {call_type == "video" && (
          <button
            type="button"
            aria-label={isVideoEnabled ? "Turn camera off" : "Turn camera on"}
            onClick={toggleVideo}
            className={`${btnBase} ${btnSize} ${isVideoEnabled ? btnOn : btnOff}`}
          >
            {isVideoEnabled ? (
              <LuVideo className="text-xl sm:text-2xl" />
            ) : (
              <LuVideoOff className="text-xl sm:text-2xl" />
            )}
          </button>
        )}

        <button
          type="button"
          aria-label={
            isRemoteMuted ? "Unmute remote sound" : "Mute remote sound"
          }
          aria-pressed={isRemoteMuted}
          onClick={toggleRemoteMute}
          className={`${btnBase} ${btnSize} ${!isRemoteMuted ? btnOn : btnOff}`}
        >
          {isRemoteMuted ? (
            <HiSpeakerXMark className="text-xl sm:text-2xl" />
          ) : (
            <HiSpeakerWave className="text-xl sm:text-2xl" />
          )}
        </button>

        <button
          type="button"
          aria-label="Open chat"
          onClick={goToChat}
          className={`${btnBase} ${btnSize} ${btnOn}`}
        >
          <IoChatbubbles className="text-xl sm:text-2xl" />
        </button>

        {isGroupCall && (
          <button
            type="button"
            aria-label="Participants"
            aria-pressed={showParticipants}
            onClick={toggleParticipants}
            className={`${btnBase} ${btnSize} ${showParticipants ? btnOff : btnOn}`}
          >
            <HiOutlineUsers className="text-xl sm:text-2xl" />
          </button>
        )}

        <button
          type="button"
          aria-label="End call"
          onClick={endCall}
          className={`${btnBase} h-12 w-16 bg-gradient-to-br from-red-500 to-red-600 text-white border-red-400/30 shadow-xl shadow-red-500/40 hover:from-red-500 hover:to-red-700 focus:ring-red-400/40 sm:h-14 sm:w-20`}
        >
          <MdOutlineCallEnd className="text-2xl sm:text-3xl" />
        </button>
      </div>
    </div>
  );
}
